import { Badge, CloseButton, Text, UnstyledButton } from '@mantine/core';

import { lovLabel } from '../engine';
import { type CatalogLabels as UiLabels } from '../labels';
import {
    loc,
    type ActiveFilters,
    type CatalogLabels,
    type CatalogPreset,
    type DateRangeValue,
    type FacetDef,
    type RangeValue
} from '../types';
import { isoToDate } from './dateIso';
import { TEXT_ACTION_CLASS } from './FacetGroup';

/** One removable chip: "Landesgesellschaft: Doka Corporate ×". */
export interface FilterChip {
    key: string;
    /** What is filtered (facet name) - rendered dimmed in front of the value. */
    group: string;
    value: string;
    onRemove: () => void;
}

interface ActiveFilterChipsProps {
    chips: FilterChip[];
    /** "Alle löschen" - only offered from two chips upward; a single chip has its own ×. */
    onClearAll?: () => void;
    L: UiLabels;
    /** Leading caption, for a chip row whose scope is not obvious from where it sits. */
    label?: string;
    /** 'outline' for chips that act on this list but belong elsewhere (the catalog filters shown
     * inside a system) - white with a dashed frame, set apart from the list's own chips at a glance. */
    variant?: 'light' | 'outline';
}

/**
 * The chip look, inline so @uag/doka-design's badge colours can't win: light grey inside, a 1px
 * dark-blue frame, slightly rounded, dark-blue text (reported: "Die Badges für die Filter machen
 * wir mit Umdasch-Dunkelblau und Rand - 1px, ganz leicht gerundet, innen leichtes Grau, Schrift
 * auch Umdasch-Dunkelblau").
 */
const chipStyle = (variant: 'light' | 'outline') => ({
    root: {
        backgroundColor: variant === 'outline' ? '#fff' : 'var(--pih-surface)',
        border: `1px ${variant === 'outline' ? 'dashed' : 'solid'} var(--pih-primary)`,
        borderRadius: 4,
        color: 'var(--pih-primary)',
        height: 28,
        paddingLeft: 10,
        fontSize: 13,
        fontWeight: 600,
        textTransform: 'none' as const
    },
    label: { overflow: 'hidden', textOverflow: 'ellipsis' }
});

/**
 * The active filters as a row of removable chips (reported: "Können wir gesetzte Filter sichtbar
 * als 'badges' darstellen die man wieder löschen kann?"). Before, the only trace of a filter was the
 * count on the filter icon - which filter, and how to get rid of just that one, needed the drawer.
 *
 * Purely presentational: the caller builds the chips (see facetFilterChips) and decides what a
 * removal means, so the catalog list, the document search and a system's Artikelliste - each with
 * its own filter state - share one look. Renders nothing without chips, so callers can place it
 * unconditionally.
 */
export const ActiveFilterChips = ({ chips, onClearAll, L, label, variant = 'light' }: ActiveFilterChipsProps) => {
    if (chips.length === 0) {
        return null;
    }
    return (
        <div aria-label={L.activeFilters} className="flex flex-wrap items-center gap-2" role="list">
            {label && (
                <Text c="dimmed" size="sm">
                    {label}
                </Text>
            )}
            {chips.map((chip) => (
                <Badge
                    key={chip.key}
                    className="pih-filter-chip"
                    data-chip-variant={variant}
                    maw="100%"
                    pr={2}
                    rightSection={
                        <CloseButton
                            aria-label={`${L.removeFilter}: ${chip.group} ${chip.value}`}
                            c="inherit"
                            size="xs"
                            variant="transparent"
                            onClick={() => chip.onRemove()}
                        />
                    }
                    role="listitem"
                    styles={chipStyle(variant)}
                    title={`${chip.group}: ${chip.value}`}
                >
                    <span style={{ fontWeight: 400 }}>{chip.group}:</span> {chip.value}
                </Badge>
            ))}
            {onClearAll && chips.length > 1 && (
                // A quiet text link - as a filled button it was the loudest thing in the toolbar.
                <UnstyledButton className={TEXT_ACTION_CLASS} ml={4} onClick={onClearAll}>
                    {L.clearAllFilters}
                </UnstyledButton>
            )}
        </div>
    );
};

const formatNumber = (value: number, locale: string) =>
    new Intl.NumberFormat(locale === 'en' ? 'en' : 'de', { maximumFractionDigits: 3 }).format(value);

const formatDate = (iso: string | undefined, locale: string) =>
    isoToDate(iso)?.toLocaleDateString(locale === 'en' ? 'en-GB' : 'de-DE') ?? iso ?? '';

/** "2,5 – 4 m", "von 2,5 m", "bis 4 m", or a single "4,2 m" for an exact match (same value twice). */
const rangeText = ({ min, max }: RangeValue, unit: string | undefined, locale: string, L: UiLabels) => {
    const suffix = unit ? ` ${unit}` : '';
    if (min != null && max != null) {
        return min === max
            ? `${formatNumber(min, locale)}${suffix}`
            : `${formatNumber(min, locale)} – ${formatNumber(max, locale)}${suffix}`;
    }
    return min != null
        ? `${L.facetFrom} ${formatNumber(min, locale)}${suffix}`
        : `${L.facetTo} ${formatNumber(max ?? 0, locale)}${suffix}`;
};

const dateRangeText = ({ from, to }: DateRangeValue, locale: string, L: UiLabels) => {
    if (from && to) {
        return `${formatDate(from, locale)} – ${formatDate(to, locale)}`;
    }
    return from ? `${L.facetFrom} ${formatDate(from, locale)}` : `${L.facetTo} ${formatDate(to, locale)}`;
};

/**
 * Chips for a facet filter set (ActiveFilters): one per selected value of a value list - so a
 * single country can be dropped from a multi-selection - and one per range, date range or switch.
 *
 * `facets` should be the FULL configured list, not just the facets currently offered: a filter
 * stays active even where its facet is hidden (e.g. a system's Artikelliste only offers facets that
 * differ inside the system), and it must still be visible and removable then.
 */
export const facetFilterChips = (
    filters: ActiveFilters,
    facets: FacetDef[],
    labels: CatalogLabels,
    locale: string,
    L: UiLabels,
    onChange: (next: ActiveFilters) => void
): FilterChip[] => {
    const without = (key: string) => {
        const next = { ...filters };
        delete next[key];
        return next;
    };
    return Object.entries(filters).flatMap(([key, value]): FilterChip[] => {
        const facet = facets.find((f) => f.key === key);
        const group = facet ? loc(facet.label, locale, key) : key;
        if (Array.isArray(value)) {
            return value.map((entry) => ({
                key: `${key}:${entry}`,
                group,
                value: facet ? lovLabel(labels, facet, entry) : entry,
                onRemove: () => {
                    const rest = value.filter((v) => v !== entry);
                    onChange(rest.length > 0 ? { ...filters, [key]: rest } : without(key));
                }
            }));
        }
        if (typeof value === 'boolean') {
            return [{ key, group, value: L.yes, onRemove: () => onChange(without(key)) }];
        }
        const isDate = facet ? facet.type === 'date-range' : 'from' in value || 'to' in value;
        const text = isDate
            ? dateRangeText(value as DateRangeValue, locale, L)
            : rangeText(value as RangeValue, facet?.unit, locale, L);
        return [{ key, group, value: text, onRemove: () => onChange(without(key)) }];
    });
};

/**
 * Chips for the table's own column filters (the funnel in each column header, see ProductGrid):
 * set there, they used to be visible only as a filled funnel - easy to overlook once the column
 * was scrolled out of view (reported: "Gesetzte Filter erscheinen in den Badges").
 *
 * `exactKeys`: columns whose filter is a picked value from a dropdown (Kategorie/System) - shown
 * as that value; every other column is a free-text "contains" filter and says so.
 */
export const columnFilterChips = (
    filters: Record<string, string>,
    labelOf: (key: string) => string,
    exactKeys: string[],
    L: UiLabels,
    onChange: (next: Record<string, string>) => void
): FilterChip[] =>
    Object.entries(filters)
        .filter(([, value]) => value.trim())
        .map(([key, value]) => ({
            key: `column:${key}`,
            group: labelOf(key),
            value: exactKeys.includes(key) ? value : L.columnFilterContains.replace('{value}', value.trim()),
            onRemove: () => {
                const next = { ...filters };
                delete next[key];
                onChange(next);
            }
        }));

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Like facetFilterChips, but a preset that is still applied exactly as configured ("Weltweites
 * Sortiment" from the landing page) shows as ONE chip under its own name - what the user ticked -
 * instead of the Landesgesellschaft/Lifecycle filters it stands for, which nobody recognised as
 * their choice (reported). Changing any of its filters breaks it up into the single chips again.
 */
export const filterChipsWithPresets = (
    filters: ActiveFilters,
    facets: FacetDef[],
    presets: CatalogPreset[],
    labels: CatalogLabels,
    locale: string,
    L: UiLabels,
    onChange: (next: ActiveFilters) => void
): FilterChip[] => {
    const preset = presets.find(
        (candidate) =>
            Object.keys(candidate.filters).length > 0 &&
            Object.entries(candidate.filters).every(([key, value]) => sameValue(filters[key], value))
    );
    if (!preset) {
        return facetFilterChips(filters, facets, labels, locale, L, onChange);
    }
    const rest = Object.fromEntries(Object.entries(filters).filter(([key]) => !(key in preset.filters)));
    return [
        {
            key: `preset:${preset.key}`,
            group: L.assortment,
            value: loc(preset.label, locale, preset.key),
            onRemove: () => onChange(rest)
        },
        ...facetFilterChips(rest, facets, labels, locale, L, (next) => onChange({ ...next, ...preset.filters }))
    ];
};
